import React, { useState } from "react";
import * as hrmsService from "../../../services/hrmsService";
import { FormDrawer } from "../hrForm";
import { EmptyState, LoadState, PrimaryButton } from "../hrComponents";
import { cardStyle, formatDate, tdStyle, thStyle, todayYmd, useLoad } from "../hrUi";
import StructureEditor, { cleanLines, emptyLine } from "./StructureEditor";
import { formatMoney } from "./payrollHelpers";

// PAY-03: an employee's salary, dated. A revision is a new row that starts on a date; the old one ends the day before.
// Only payroll admins see this tab.
export default function EmployeeSalaryTab({ employeeId, currency }) {
  const history = useLoad(() => hrmsService.getSalaryHistory(employeeId), [employeeId]);
  const templates = useLoad(() => hrmsService.listSalaryTemplates(), []);
  const components = useLoad(() => hrmsService.listPayComponents(), []);
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState([emptyLine()]);
  const rows = history.data || [];
  const current = rows[0];

  const start = () => {
    setLines(
      current && current.components.length
        ? current.components.map((c) => ({
            component_id: c.component_id,
            calc_type: c.calc_type,
            amount: c.calc_type === "FIXED" ? c.amount ?? c.monthly_amount : "",
            percent: c.calc_type === "PERCENT" ? c.percent : "",
            percent_of: c.percent_of || "CTC",
          }))
        : [emptyLine()]
    );
    setOpen(true);
  };
  const templateOptions = (templates.data || []).filter((t) => t.status === "ACTIVE").map((t) => ({ label: `${t.name} (${t.line_count} components)`, value: t.id }));

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div style={{ fontSize: 13, color: "#6B7280" }}>Monthly amounts in {currency || "your currency"}. Only payroll administrators can see this.</div>
        <PrimaryButton onClick={start}>{rows.length ? "Revise salary" : "Set salary"}</PrimaryButton>
      </div>
      <LoadState loading={history.loading} error={history.error} onRetry={history.reload}>
        {rows.length === 0 ? (
          <EmptyState title="No salary set" text="This person cannot be paid in a payroll run until a salary is set." />
        ) : (
          rows.map((s) => (
            <div key={s.id} style={{ ...cardStyle, marginBottom: 12 }}>
              <div className="d-flex flex-wrap justify-content-between align-items-center" style={{ padding: "12px 16px", borderBottom: "1px solid #F3F4F6" }}>
                <div>
                  <strong>{currency} {formatMoney(s.ctc_monthly)}</strong> <span style={{ color: "#6B7280", fontSize: 13 }}>a month</span>
                </div>
                <div style={{ fontSize: 13, color: "#6B7280" }}>
                  {formatDate(s.effective_from)} – {s.effective_to ? formatDate(s.effective_to) : "now"}
                  {s.reason ? ` · ${s.reason}` : ""}
                </div>
              </div>
              <table className="table mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Component</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Monthly</th>
                  </tr>
                </thead>
                <tbody>
                  {s.components.map((c) => (
                    <tr key={c.code}>
                      <td style={tdStyle}>{c.name}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{formatMoney(c.monthly_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </LoadState>

      <FormDrawer
        show={open}
        title={rows.length ? "Revise salary" : "Set salary"}
        width={560}
        initial={
          current
            ? { effective_from: todayYmd(), ctc_monthly: current.ctc_monthly, mode: current.template_id ? "TEMPLATE" : "CUSTOM", template_id: current.template_id || undefined }
            : { effective_from: todayYmd(), mode: templateOptions.length ? "TEMPLATE" : "CUSTOM" }
        }
        fields={(v) => [
          { key: "effective_from", label: "Starts on", type: "date", required: true, hint: "Must be after the current salary's start date" },
          { key: "ctc_monthly", label: "Monthly CTC", type: "number", step: "0.01", min: "0", required: true },
          { key: "mode", label: "Split it by", type: "select", options: [{ label: "A salary template", value: "TEMPLATE" }, { label: "Components I choose", value: "CUSTOM" }] },
          ...(v.mode === "TEMPLATE" ? [{ key: "template_id", label: "Template", type: "select", options: templateOptions, required: true }] : []),
          { key: "reason", label: "Reason", max: 200, hint: "For example: annual revision" },
        ]}
        onHide={() => setOpen(false)}
        onSave={(clean) => {
          const { mode, ...rest } = clean;
          return hrmsService.setSalary(employeeId, { ...rest, ...(mode === "CUSTOM" ? { lines: cleanLines(lines) } : {}) });
        }}
        onDone={history.reload}
      >
        {(values) => (values.mode === "CUSTOM" ? <StructureEditor lines={lines} onChange={setLines} components={components.data || []} /> : null)}
      </FormDrawer>
    </>
  );
}
